/**
 * @fileoverview Header ClickToAction button component with fixed styling
 */

import React from 'react';

/**
 * Type for the ClickToAction button variants
 */
export type ClickToActionVariant = 'mentoring' | 'newsletter';

/**
 * Props for the HeaderClickToAction component
 */
interface HeaderClickToActionProps {
  /**
   * The variant of the ClickToAction button
   */
  variant: ClickToActionVariant;
  
  /**
   * Optional click handler
   */
  onClick?: () => void;
  
  /**
   * Optional additional classes
   */
  className?: string;
  
  /**
   * Optional disabled state
   */
  disabled?: boolean;
}

/**
 * Configuration for ClickToAction variants with correct Tailwind classes
 */
const CLICK_TO_ACTION_VARIANTS = {
  mentoring: {
    text: 'Mentoring',
    baseClasses: 'bg-secondary-red hover:bg-secondary-red-dark border-2 border-neutral-700' // Using exact HEX colors
  },
  newsletter: {
    text: 'Newsletter',
    baseClasses: 'bg-secondary-blue hover:bg-secondary-blue-dark border-2 border-neutral-700' // Using exact HEX colors
  }
} as const;

/**
 * Header ClickToAction Button component
 * 
 * @component
 * @example
 * ```tsx
 * <HeaderClickToAction variant="mentoring" onClick={() => {}} />
 * ```
 */
export const HeaderClickToAction: React.FC<HeaderClickToActionProps> = ({
  variant,
  onClick,
  className = '',
  disabled = false
}) => {
  const variantConfig = CLICK_TO_ACTION_VARIANTS[variant];
  
  // Basic styling without transitions (to match your design)
  const baseClasses = "px-6 py-2 rounded-full text-white font-bold";
  const disabledClasses = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        ${baseClasses}
        ${variantConfig.baseClasses}
        ${disabledClasses}
        ${className}
      `.trim()}
      data-testid={`header-click-to-action-${variant}`}
    >
      {variantConfig.text}
    </button>
  );
};

HeaderClickToAction.displayName = 'HeaderClickToAction';

export type { HeaderClickToActionProps };